// Input sanitization utilities

// HTML entities that need escaping
const HTML_ENTITIES: Record<string, string> = {
  '&': '&amp;',
  '<': '&lt;',
  '>': '&gt;',
  '"': '&quot;',
  "'": '&#x27;',
  '/': '&#x2F;'
};

// Regular expressions for validation
const PATTERNS = {
  email: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
  url: /^https?:\/\/(www\.)?[-a-zA-Z0-9@:%._\+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b([-a-zA-Z0-9()@:%_\+.~#?&//=]*)$/,
  alphanumeric: /^[a-zA-Z0-9]+$/,
  numeric: /^[0-9]+$/,
  username: /^[a-zA-Z0-9_]{3,20}$/,
  tenantCode: /^[A-Z0-9]{4,10}$/,
  guid: /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i,
  phoneNumber: /^[\d\s\-\+\(\)]+$/,
  safeString: /^[a-zA-Z0-9\s\-_.,!?()]+$/
};

// Dangerous patterns to block
const DANGEROUS_PATTERNS = [
  /<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi,
  /javascript:/gi,
  /on\w+\s*=/gi, // Event handlers like onclick=
  /<iframe/gi,
  /<object/gi,
  /<embed/gi,
  /eval\(/gi,
  /expression\(/gi,
  /vbscript:/gi,
  /data:text\/html/gi
];

export class InputSanitizer {
  // Escape HTML entities to prevent XSS
  static escapeHtml(input: string): string {
    if (typeof input !== 'string') return '';
    
    return input.replace(/[&<>"'\/]/g, (match) => HTML_ENTITIES[match] || match);
  }

  // Remove all HTML tags
  static stripHtml(input: string): string {
    if (typeof input !== 'string') return '';
    
    return input.replace(/<[^>]*>/g, '');
  }

  // Sanitize for display (keeps safe HTML)
  static sanitizeForDisplay(input: string): string {
    if (typeof input !== 'string') return '';
    
    // First escape dangerous characters
    let sanitized = this.escapeHtml(input);
    
    // Allow only safe tags
    const safeTags = ['b', 'i', 'em', 'strong', 'u', 'br', 'p', 'span'];
    safeTags.forEach(tag => {
      const regex = new RegExp(`&lt;(\/?)${tag}&gt;`, 'gi');
      sanitized = sanitized.replace(regex, '<$1' + tag + '>');
    });
    
    return sanitized;
  }

  // Validate and sanitize email
  static sanitizeEmail(input: string): string | null {
    if (typeof input !== 'string') return null;
    
    const trimmed = input.trim().toLowerCase();
    if (!PATTERNS.email.test(trimmed)) return null;
    
    return trimmed;
  }

  // Validate and sanitize URL
  static sanitizeUrl(input: string): string | null {
    if (typeof input !== 'string') return null;
    
    const trimmed = input.trim();
    if (!PATTERNS.url.test(trimmed)) return null;
    
    // Additional check for malicious URLs
    if (DANGEROUS_PATTERNS.some(pattern => pattern.test(trimmed))) {
      return null;
    }
    
    return trimmed;
  }

  // Sanitize username
  static sanitizeUsername(input: string): string | null {
    if (typeof input !== 'string') return null;
    
    const trimmed = input.trim();
    if (!PATTERNS.username.test(trimmed)) return null;
    
    return trimmed;
  }

  // Sanitize tenant code
  static sanitizeTenantCode(input: string): string | null {
    if (typeof input !== 'string') return null;
    
    const trimmed = input.trim().toUpperCase();
    if (!PATTERNS.tenantCode.test(trimmed)) return null;
    
    return trimmed;
  }

  // Sanitize numeric input
  static sanitizeNumber(input: any, min?: number, max?: number): number | null {
    const num = Number(input);
    
    if (isNaN(num) || !isFinite(num)) return null;
    
    if (min !== undefined && num < min) return null;
    if (max !== undefined && num > max) return null;
    
    return num;
  }

  // Sanitize boolean
  static sanitizeBoolean(input: any): boolean {
    return input === true || input === 'true' || input === 1 || input === '1';
  }

  // Sanitize array
  static sanitizeArray<T>(
    input: any,
    itemSanitizer: (item: any) => T | null,
    maxLength?: number
  ): T[] {
    if (!Array.isArray(input)) return [];
    
    const sanitized = input
      .slice(0, maxLength || 1000)
      .map(itemSanitizer)
      .filter(item => item !== null) as T[];
    
    return sanitized;
  }

  // Sanitize object
  static sanitizeObject<T extends Record<string, any>>(
    input: any,
    schema: Record<keyof T, (value: any) => any>
  ): Partial<T> | null {
    if (typeof input !== 'object' || input === null) return null;
    
    const sanitized: Partial<T> = {};
    
    for (const [key, sanitizer] of Object.entries(schema)) {
      if (key in input) {
        const value = sanitizer(input[key]);
        if (value !== null && value !== undefined) {
          sanitized[key as keyof T] = value;
        }
      }
    }
    
    return sanitized;
  }

  // Check for dangerous content
  static containsDangerousContent(input: string): boolean {
    if (typeof input !== 'string') return false;
    
    return DANGEROUS_PATTERNS.some(pattern => pattern.test(input));
  }

  // Sanitize file name
  static sanitizeFileName(input: string): string | null {
    if (typeof input !== 'string') return null;
    
    // Remove path components
    const filename = input.split(/[\/\\]/).pop() || '';
    
    // Remove dangerous characters
    const sanitized = filename.replace(/[^a-zA-Z0-9._\-]/g, '_');
    
    // Check for dangerous extensions
    const dangerousExtensions = ['.exe', '.bat', '.cmd', '.com', '.pif', '.scr', '.vbs', '.js'];
    if (dangerousExtensions.some(ext => sanitized.toLowerCase().endsWith(ext))) {
      return null;
    }
    
    return sanitized;
  }

  // Sanitize search query
  static sanitizeSearchQuery(input: string, maxLength: number = 100): string {
    if (typeof input !== 'string') return '';
    
    // Remove special characters that could be used for injection
    let sanitized = input
      .trim()
      .replace(/[<>'"]/g, '')
      .slice(0, maxLength);
    
    // Remove SQL-like keywords
    const sqlKeywords = ['SELECT', 'INSERT', 'UPDATE', 'DELETE', 'DROP', 'UNION', 'EXEC', 'SCRIPT'];
    sqlKeywords.forEach(keyword => {
      const regex = new RegExp(`\\b${keyword}\\b`, 'gi');
      sanitized = sanitized.replace(regex, '');
    });
    
    return sanitized.trim();
  }

  // Sanitize JSON string
  static sanitizeJson(input: string): any | null {
    if (typeof input !== 'string') return null;
    
    try {
      const parsed = JSON.parse(input);
      // Additional validation can be added here
      return parsed;
    } catch (error) {
      return null;
    }
  }

  // Sanitize for RegExp
  static escapeRegExp(input: string): string {
    if (typeof input !== 'string') return '';
    
    return input.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  }
}

// Form data sanitizer for common form fields
export class FormDataSanitizer {
  static sanitizeLoginForm(data: any): {
    username?: string;
    password?: string;
    rememberMe?: boolean;
  } | null {
    const result = InputSanitizer.sanitizeObject(data, {
      username: (v) => InputSanitizer.sanitizeUsername(v) || InputSanitizer.sanitizeEmail(v),
      password: (v) => typeof v === 'string' ? v : null, // Don't modify passwords
      rememberMe: (v) => InputSanitizer.sanitizeBoolean(v)
    });
    
    if (!result) return null;
    
    return {
      username: result.username as string | undefined,
      password: result.password as string | undefined,
      rememberMe: result.rememberMe as boolean | undefined
    };
  }

  static sanitizeTenantForm(data: any): {
    tenantCode?: string;
  } | null {
    const result = InputSanitizer.sanitizeObject(data, {
      tenantCode: (v) => InputSanitizer.sanitizeTenantCode(v)
    });
    
    if (!result) return null;
    
    return {
      tenantCode: result.tenantCode as string | undefined
    };
  }

  static sanitizeQuestionData(data: any): any {
    if (!data || typeof data !== 'object') return null;
    
    return {
      ...data,
      // Sanitize text fields
      title: data.title ? InputSanitizer.stripHtml(data.title) : undefined,
      description: data.description ? InputSanitizer.sanitizeForDisplay(data.description) : undefined,
      // Sanitize arrays
      choices: data.choices ? InputSanitizer.sanitizeArray(
        data.choices,
        (choice: any) => ({
          ...choice,
          text: choice.text ? InputSanitizer.stripHtml(choice.text) : ''
        })
      ) : undefined
    };
  }
}

// Request body sanitizer middleware
export function sanitizeRequestBody(schema: Record<string, (value: any) => any>) {
  return (data: any): any => {
    if (!data || typeof data !== 'object') return {};
    
    const sanitized = InputSanitizer.sanitizeObject(data, schema);
    
    if (!sanitized || Object.keys(sanitized).length === 0) {
      throw new Error('Invalid request data');
    }
    
    return sanitized;
  };
}

// URL parameter sanitizer
export function sanitizeUrlParams(params: URLSearchParams): URLSearchParams {
  const sanitized = new URLSearchParams();
  
  for (const [key, value] of params.entries()) {
    // Sanitize key
    const safeKey = key.replace(/[^a-zA-Z0-9_\-]/g, '');
    
    // Sanitize value based on common parameter types
    let safeValue = value;
    
    if (safeKey.includes('id') || safeKey.includes('Id')) {
      // Validate GUID
      if (!PATTERNS.guid.test(value)) continue;
    } else if (safeKey.includes('page') || safeKey.includes('count') || safeKey.includes('limit')) {
      // Validate number
      const num = InputSanitizer.sanitizeNumber(value, 0, 10000);
      if (num === null) continue;
      safeValue = num.toString();
    } else {
      // General string sanitization
      safeValue = InputSanitizer.sanitizeSearchQuery(value);
    }
    
    if (safeKey && safeValue) {
      sanitized.set(safeKey, safeValue);
    }
  }
  
  return sanitized;
}